// Camera-capture engine for the real Phase-2C self-capture client.
//
// Adapted from the archived in-house capture prototype (`reference/scanner/`).
// Covers the 6-shot state machine, rear-camera loop, and center-crop→JPEG
// capture, wired to the presigned-PUT upload contract. Live onnxruntime-web
// guidance (foot/paper/angle detection) runs per frame during capture (see
// `guidance.ts` + `CaptureFlow.tsx`): it drives the banners and disables the
// shutter unless guidance reports "good" (foot + paper detected, correct foot,
// paper framed, and — when the device reports orientation — the tilt angle in
// the photo's window, `ANGLE_WINDOWS`). This is stricter than the prototype, which gated the shutter on
// angle alone. Guidance is best-effort — capture degrades to manual (shutter
// always enabled) when the model can't load or the device exposes no
// orientation.
//
// Wire contract preserved from the prototype: 3 photos per foot (inside / front /
// outside), so 6 JPEGs total, named `{side}_photo{1,2,3}.jpg`. That exact
// filename shape is what footscan-server's ported `run_inference` groups on
// (by foot + photoN) to feed the reconstruction (→ `model_{l,r}.obj`). Do not
// change the naming without changing footscan-server (Subtask 5 port).
//
// Each shot also uploads its unclipped frame as `{side}_full{n}.jpg` for
// footscan-server's IBV step. That name deliberately carries no
// `photoN` token, so footscan-server's grouping skips it instead of letting it
// overwrite the square.

import { diagEnabled, trackPresetOverride, type TrackPreset } from "./config";
import type { DeviceInfo } from "./device";
import type { GuidanceSeverity } from "./guidance";
import { versionedPublicUrl } from "./public-asset-url";
import type { RunInput } from "./upload-pipeline";

export type Side = "left" | "right";

// Per-foot capture angles, in the prototype's fixed order. The array index (+1)
// becomes the photoN suffix, so inside=photo1, front=photo2, outside=photo3 —
// matching the prototype build that produced footscan-server's training photos.
export type AngleName = "inside" | "front" | "outside";
const ANGLE_ORDER: readonly AngleName[] = ["inside", "front", "outside"] as const;

export interface CaptureStep {
  /** Stable id, e.g. "left-inside". Mirrors the prototype's `step` field. */
  id: string;
  side: Side;
  angle: AngleName;
  /** 1|2|3 — per-foot photo number, drives the filename + backend grouping. */
  photoIndex: 1 | 2 | 3;
  /** Flat upload filename, e.g. "left_photo1.jpg" (no slashes — regex-safe). */
  filename: string;
  /** i18n key for the step name, e.g. "steps.leftInside.name". */
  nameKey: string;
  /** i18n key for the framing instruction (value may contain <strong>). */
  instructionKey: string;
  /** Per-step guide-overlay SVG, served from public/guides/ (prototype overlays), with ?v=<content hash>. */
  guideUrl: string;
}

// "left" + "inside" → "leftInside" — the camelCase segment the prototype's i18n
// keys (`steps.leftInside.name`) and this repo's `i18n/locales/*.json` use.
function stepKeyBase(side: Side, angle: AngleName): string {
  return `steps.${side}${angle[0].toUpperCase()}${angle.slice(1)}`;
}

function buildSteps(): CaptureStep[] {
  const sides: readonly Side[] = ["left", "right"] as const;
  return sides.flatMap((side) =>
    ANGLE_ORDER.map((angle, i) => {
      const photoIndex = (i + 1) as 1 | 2 | 3;
      const keyBase = stepKeyBase(side, angle);
      return {
        id: `${side}-${angle}`,
        side,
        angle,
        photoIndex,
        filename: `${side}_photo${photoIndex}.jpg`,
        nameKey: `${keyBase}.name`,
        instructionKey: `${keyBase}.instruction`,
        guideUrl: versionedPublicUrl(`/guides/${side}-${angle}.svg`),
      } satisfies CaptureStep;
    }),
  );
}

/** The 6 ordered capture steps: left inside/front/outside, then right. */
export const CAPTURE_STEPS: readonly CaptureStep[] = buildSteps();

/** A captured frame paired with the step it satisfies. */
export interface CaptureResult {
  step: CaptureStep;
  blob: Blob;
  /**
   * Camera tilt angle (degrees, from deviceorientation) at the moment of the
   * shot, or null when the device exposes no orientation. The archived
   * prototype recorded this per photo (`angle` on each upload entry) — kept so
   * the backend can use it as a pose prior if it ever wants to.
   */
  angle?: number | null;
  /** The unclipped frame (see `fullFrameFilename`). */
  fullBlob?: Blob;
  /** Gravity vector in g at the shot, null without motion data (see `gravity.ts`). */
  gravity?: [number, number, number] | null;
}

/**
 * Per-photo gravity vectors keyed by upload filename, e.g.
 * `{ "left_photo1.jpg": [0.01, 0.7, 0.71] }`. Same shape and omission rule as
 * `captureAngles`; the full frame of a shot shares its square's reading.
 */
export function captureGravity(
  results: readonly CaptureResult[],
): Record<string, [number, number, number]> {
  const out: Record<string, [number, number, number]> = {};
  for (const r of results) {
    if (r.gravity) out[r.step.filename] = r.gravity;
  }
  return out;
}

/** Upload name of a step's unclipped frame, e.g. "left_full1.jpg". */
export function fullFrameFilename(step: CaptureStep): string {
  return `${step.side}_full${step.photoIndex}.jpg`;
}

/**
 * Whether an upload may fail without holding the scan back. Only the full
 * frames: footscan-server's own solver reads the squares, the full frames feed
 * the IBV comparison alone, which skips a scan with any of them missing. At
 * ~2.6 MB each they are also the PUTs most likely to time out on a weak uplink,
 * and blocking the customer on them would cost a scan that solves fine.
 */
export function isOptionalUpload(filename: string): boolean {
  return /^(left|right)_full[1-3]\.jpg$/.test(filename);
}

/**
 * Per-photo capture angles keyed by upload filename, e.g.
 * `{ "left_photo1.jpg": 43 }`. Photos without an orientation reading are
 * omitted; an empty map means the device reported no orientation at all.
 */
export function captureAngles(results: readonly CaptureResult[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const r of results) {
    if (typeof r.angle === "number" && Number.isFinite(r.angle)) {
      out[r.step.filename] = r.angle;
    }
  }
  return out;
}


// Camera track requests. Every scan asks for the full 4:3 sensor frame
// 4032×3024: IBV gets the unclipped frame and rejects our centre square, which
// cuts the sheet corners, and an iPhone delivers 3024×4032 at zoom 1, wider
// than any 16:9 mode (footscan-webapp #142, preview scans 5JYRA3, ON5OBU,
// D5VQT3). The size is still re-APPLIED to an open track (the exact-environment
// success path opens with no size hint and would otherwise sit at ~480; see
// applyCaptureResolution / issue #56). `?track=` picks another candidate for
// on-device checks; `720` is the former 1280×720 default, kept because asking
// for 1920×1080 made iPhones switch to a cropped, zoomed sensor mode.
const TRACK_PRESETS: Record<TrackPreset, MediaTrackConstraints> = {
  "720": { width: { ideal: 1280 }, height: { ideal: 720 } },
  "43": { aspectRatio: { ideal: 4 / 3 } },
  "960": { width: { ideal: 960 }, height: { ideal: 720 }, aspectRatio: { ideal: 4 / 3 } },
  "1440": { width: { ideal: 1440 }, height: { ideal: 1080 }, aspectRatio: { ideal: 4 / 3 } },
  "1920": { width: { ideal: 1920 }, height: { ideal: 1440 }, aspectRatio: { ideal: 4 / 3 } },
  "4032": { width: { ideal: 4032 }, height: { ideal: 3024 }, aspectRatio: { ideal: 4 / 3 } },
};

// Phones at or under this much RAM (`navigator.deviceMemory`, Chromium only)
// ask for 1920×1440 instead. On a Redmi Note 11 (4 GB, Adreno 610) the 4032
// request delivered 3264×2448 and the live guidance stuttered, because every
// inference frame scales that whole frame down (preview scan DUQ5U4,
// 2026-10-19). IBV still rated 1440×1080 bands quality 7. Safari exposes no
// deviceMemory, so iPhones keep 4032.
const LOW_MEMORY_GB = 4;

/** Whether this device should get the lighter 1920×1440 track by default. */
export function lowEndDevice(nav: Navigator | undefined = globalThis.navigator): boolean {
  const gb = (nav as (Navigator & { deviceMemory?: number }) | undefined)?.deviceMemory;
  return typeof gb === "number" && gb <= LOW_MEMORY_GB;
}

// Crash fallback for the devices lowEndDevice cannot see (Safari reports no
// deviceMemory). The capture arms a mark while the camera runs and disarms it
// on every orderly exit (armCrashGuard/disarmCrashGuard). A page that loads with
// the mark still set lost its tab mid-capture, so this device remembers it and
// runs in crashSafeMode (no model) from then on. Arming at the shutter itself was too late:
// Safari persists localStorage lazily, and the tab died before the write landed
// (iPhone XS, preview 2026-10-20).
const CAPTURE_INFLIGHT_KEY = "hezo.capture.inflight";
// New name since #147: the crashes remembered under the old one came from the
// asyncify build's leak (fixed there), so those devices get the model back once.
// A device that still crashes is caught again under this key.
const SAFE_MODE_KEY = "hezo.capture.safeMode";
const STALE_SAFE_MODE_KEY = "hezo.capture.lightTrack";

export interface CrashState {
  /** This device lost a tab during a capture before: run without the model. */
  safeMode: boolean;
  /** The crash happened on the load right before this one: tell the customer. */
  justCrashed: boolean;
}

const NO_CRASH: CrashState = { safeMode: false, justCrashed: false };

/** Read (and settle) this device's crash marks. Uncached; see crashState. */
export function readCrashState(store: Storage | undefined = safeLocalStorage()): CrashState {
  if (!store) return NO_CRASH;
  try {
    store.removeItem(STALE_SAFE_MODE_KEY);
    const justCrashed = store.getItem(CAPTURE_INFLIGHT_KEY) !== null;
    if (justCrashed) {
      store.removeItem(CAPTURE_INFLIGHT_KEY);
      store.setItem(SAFE_MODE_KEY, "1");
    }
    return { safeMode: store.getItem(SAFE_MODE_KEY) === "1", justCrashed };
  } catch {
    return NO_CRASH;
  }
}

/** Mark a capture in flight; a load that finds it set means the tab crashed. */
export function armCrashGuard(store: Storage | undefined = safeLocalStorage()): void {
  try {
    store?.setItem(CAPTURE_INFLIGHT_KEY, "1");
  } catch {
    /* private mode or full storage: no fallback, the capture still runs */
  }
}

/** Clear the mark on an orderly exit (capture done, tab hidden or closed). */
export function disarmCrashGuard(store: Storage | undefined = safeLocalStorage()): void {
  try {
    store?.removeItem(CAPTURE_INFLIGHT_KEY);
  } catch {
    /* see armCrashGuard */
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

// Read once per page load, at the first use (capture mount), before this load
// arms its own mark. A later call (diag refresh, camera switch) would otherwise
// mistake the running capture for a crash.
let crashedBeforeThisLoad: CrashState | undefined;

function crashState(): CrashState {
  crashedBeforeThisLoad ??= readCrashState();
  return crashedBeforeThisLoad;
}

/**
 * Whether this device lost its tab during a capture before. It then runs
 * without the in-browser model; the camera track stays as it was. An iPhone XS
 * crashed at the shutter with the guidance on at 720, 1920 and 4032 alike, and
 * ran clean without it at 1920 and at 4032 (preview scans G9D1SE, 0KBZZR,
 * INLTB2, 2026-10-20). The tilt still gates the shutter.
 */
export function crashSafeMode(): boolean {
  return crashState().safeMode;
}

/** Whether the tab died during the capture right before this load (the photos are gone). */
export function crashNoticeDue(): boolean {
  return crashState().justCrashed;
}

function activeTrackPreset(loc: Location): TrackPreset {
  return trackPresetOverride(loc) ?? (lowEndDevice() ? "1920" : "4032");
}

/**
 * The size constraints every camera path requests: 4032×3024 (4:3), or
 * 1920×1440 on a low-memory device (see LOW_MEMORY_GB), unless `?track=` asks
 * for another candidate. Shared because we request the rear lens
 * in several escalating ways (see startRearCamera) and a manual camera switch
 * must keep the same request.
 */
export function captureVideoSize(loc: Location = window.location): MediaTrackConstraints {
  return TRACK_PRESETS[activeTrackPreset(loc)];
}

/** Short label of the active track request for the diag panel, e.g. "4032×3024 (4:3)". */
export function trackRequestLabel(loc: Location = window.location): string {
  const c = TRACK_PRESETS[activeTrackPreset(loc)];
  const w = (c.width as ConstrainULongRange | undefined)?.ideal;
  const h = (c.height as ConstrainULongRange | undefined)?.ideal;
  if (!w || !h) return "4:3 aspect only";
  return c.aspectRatio ? `${w}×${h} (4:3)` : `${w}×${h}`;
}

// The centre-square crop must stay at/above this on its short side, or the
// paper is too coarse for a metric-accurate solve. 720 is the floor the
// footscan-server recon was validated against; below it the upload is silently
// unusable (issue #56), so the shutter is gated on it.
export const MIN_CAPTURE_DIMENSION = 720;

// Ceiling for the uploaded centre square's side. footscan-server solves at
// 1024 px on the short edge and measures blur at 720, so a 3024 px square from
// the 4032 track only costs upload (~1.9 MB a photo, ~0.37 MB at 1440). The blur
// measure moved under 1 % on six preview squares scaled 3024 -> 1440.
export const MAX_SQUARE_DIMENSION = 1440;

/** Whether a `width`×`height` frame is sharp enough to capture (see MIN_CAPTURE_DIMENSION). */
export function captureResolutionOk(width: number, height: number): boolean {
  return Math.min(width, height) >= MIN_CAPTURE_DIMENSION;
}

/**
 * Pull the delivered resolution up AFTER the stream is open. Pairing a size
 * hint with `{ exact: "environment" }` in the *initial* getUserMedia trips
 * OverconstrainedError on some iPhones (see CAMERA_CONSTRAINTS), but the same
 * hint applied to an already-open track does not — iOS just re-negotiates to
 * the nearest supported mode. Without this, the exact-environment success path
 * left the track at the device default (~480 wide on iPhone), which capped real
 * captures at 480×480 and starved the A4-pose solve (issue #56). Best effort:
 * a reject leaves the current resolution untouched.
 */
async function applyCaptureResolution(stream: MediaStream): Promise<MediaStream> {
  const track = stream.getVideoTracks()[0];
  if (track?.applyConstraints) {
    try {
      await track.applyConstraints(captureVideoSize());
    } catch (err) {
      if (diagEnabled()) console.warn("[camera] applyConstraints(size) failed", err);
    }
  }
  if (diagEnabled()) {
    const s = track?.getSettings?.() ?? {};
    console.log(`[camera] resolution after applyConstraints=${s.width ?? "?"}×${s.height ?? "?"}`);
  }
  return stream;
}

// Preferred constraints: HARD-require the rear lens via `{ exact }`. NOTE: no
// width/height here — pairing size hints with `{ exact: "environment" }` trips
// OverconstrainedError on some iPhones (observed on-device), which then dumped
// us onto the front camera. Size is only an `ideal` hint anyway; we re-apply it
// on the deviceId/advisory paths.
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: { exact: "environment" } },
  audio: false,
};

// Fallback when `{ exact }` throws: advisory facingMode + size. On a well-behaved
// device this is the rear camera; on the broken-iOS case it can still be the
// FRONT camera, which startRearCamera then corrects by deviceId.
function cameraConstraintsFallback(): MediaStreamConstraints {
  return { video: { facingMode: "environment", ...captureVideoSize() }, audio: false };
}

// Rear-camera label match across the locales we ship (en/de/es/it/ar) plus a
// few common extras — iOS returns *localised* device labels ("Rückkamera",
// "Frontkamera", …), so English-only matching would miss the back lens.
const REAR_LABEL_RE =
  /back|rear|environment|rück|arrière|arriere|posteriore|trasera|traseira|خلف|الخلفية/i;

// Front-camera label match (same localisation problem as the rear regex). Used
// by the switcher to jump straight to the selfie lens instead of blindly
// cycling by device index — modern phones enumerate several REAR lenses, so an
// index cycle lands on another back camera before the front one.
const FRONT_LABEL_RE =
  /front|user|face|selfie|vorder|frontale|frontal|delantera|أمام|الأمامية/i;

// Rear lenses the switcher SKIPS when picking the "back" camera: the ultra-wide
// and telephoto/periscope variants some phones expose as separate devices. The
// ultra-wide's heavy barrel distortion breaks the A4-corner solvePnP pose that
// gives the reconstruction its metric scale, and the telephoto can't frame the
// whole sheet — the plain main wide lens is the one the scan is calibrated for.
// Best-effort on the localised label ("Ultraweitwinkel", "Telephoto", …); note
// this deliberately does NOT match "Weitwinkel"/"wide" alone (that is the main
// lens on iOS). No match → we keep whatever rear lens we found.
const REAR_AVOID_RE = /ultra|tele|zoom|periscope/i;

export interface CropRect {
  sx: number;
  sy: number;
  size: number;
}

/**
 * Largest centered square that fits in a `width`×`height` frame. Pure — the
 * geometry the prototype used to center-crop each frame before capture/inference.
 */
export function centerCropRect(width: number, height: number): CropRect {
  const size = Math.min(width, height);
  return {
    sx: Math.floor((width - size) / 2),
    sy: Math.floor((height - size) / 2),
    size,
  };
}

/**
 * Fraction of the sensor's width the centre-square crop keeps —
 * `size / max(width, height)`. footscan-server divides the focal length by this
 * so the paper-anchored pose is metrically correct regardless of the device's
 * frame aspect ratio (footscan-server #45). 16:9 → 0.5625, 4:3 → 0.75, a truly
 * square track → 1. The server can't recover this from the square JPEG, so we
 * send it; older builds omit it and the server falls back to its 16:9 assumption.
 *
 * Measured against the LONG axis, NOT the delivered width. `sensor_width_mm` in
 * the server's `focal_px = f_mm / (sensor_width_mm * crop) * px` is the sensor's
 * long dimension, and which axis the browser calls "width" says nothing about
 * how the sensor was read out: iOS hands this app a 720×1280 PORTRAIT track, so
 * dividing by the width returned 1.0 while the square really spans 720/1280 of
 * the sensor. That collapsed the pipeline onto legacy's known-wrong full-frame
 * scale and made every length ~17% too large — 313mm for a ruler-measured 265mm
 * foot, against 264mm from the prototype, which had always used min/max
 * (`tools/capture_ab/bundles.py`). Confirmed on the paired capture-A/B run
 * `capture-ab/sebidunkel/` (2026-07-31).
 */
export function sensorCropFraction(width: number, height: number): number {
  return centerCropRect(width, height).size / Math.max(width, height);
}

// Log the lens we actually got (gated behind ?diag=1 / DEBUG). iOS sometimes
// hands back the front camera even for an "environment" request, so seeing the
// real facingMode/label is how we tell whether auto-selection worked.
function logAcquired(stream: MediaStream, via: string): MediaStream {
  if (diagEnabled()) {
    const track = stream.getVideoTracks()[0];
    const s = track?.getSettings?.() ?? {};
    console.log(
      `[camera] via ${via} · facingMode=${s.facingMode ?? "?"} · label=${track?.label ?? "?"}`,
    );
  }
  return stream;
}

/** True if the stream's active video track is the rear/environment lens. */
function isRearStream(stream: MediaStream): boolean {
  return stream.getVideoTracks()[0]?.getSettings?.().facingMode === "environment";
}

/**
 * Find the rear camera's deviceId by label (facingMode is unreliable on iOS).
 * Prefers a label that matches the multilingual rear regex; otherwise, on a
 * 2-camera phone, falls back to "the one that isn't the current (front) lens".
 * Returns null if nothing better than the current camera is found.
 */
async function findRearCameraDeviceId(currentStream: MediaStream): Promise<string | null> {
  const cams = await listVideoInputs();
  const currentId = currentStream.getVideoTracks()[0]?.getSettings?.().deviceId;
  const labelled = cams.find((c) => REAR_LABEL_RE.test(c.label));
  if (labelled) return labelled.deviceId;
  const other = cams.find((c) => c.deviceId && c.deviceId !== currentId);
  return other?.deviceId ?? null;
}

/**
 * Open the rear camera, as reliably as the platform allows:
 *   1. `{ exact: "environment" }` — works on Android/desktop and well-behaved iOS.
 *   2. On failure, take any camera (advisory) to obtain permission; if that's
 *      the FRONT lens (broken-iOS case, observed on-device), re-open the rear
 *      lens by deviceId, matched via localised label.
 * The UI also offers a manual switch (`listVideoInputs`/`startCameraByDeviceId`)
 * as the final fallback. Rejects if permission is denied.
 */
export async function startRearCamera(): Promise<MediaStream> {
  const md = navigator.mediaDevices;
  if (!md?.getUserMedia) {
    throw new Error("This browser does not support camera capture.");
  }
  try {
    const stream = logAcquired(await md.getUserMedia(CAMERA_CONSTRAINTS), "exact-environment");
    return await applyCaptureResolution(stream);
  } catch (err) {
    // `{ exact }` unsatisfiable (desktop with no rear cam, or the iOS quirk) →
    // OverconstrainedError/NotFoundError. Permission denials (NotAllowedError)
    // are rethrown — a retry would only prompt again and fail.
    const name = (err as { name?: string })?.name;
    if (diagEnabled()) {
      console.warn(`[camera] exact-environment failed (${name ?? String(err)}) — falling back`);
    }
    if (name !== "OverconstrainedError" && name !== "NotFoundError") throw err;
  }

  const stream = logAcquired(await md.getUserMedia(cameraConstraintsFallback()), "advisory-fallback");
  if (isRearStream(stream)) return await applyCaptureResolution(stream);

  // iOS handed us the front camera despite asking for "environment" — hunt for
  // the rear lens by deviceId and switch to it.
  const rearId = await findRearCameraDeviceId(stream);
  if (rearId) {
    try {
      const rear = await startCameraByDeviceId(rearId);
      stopStream(stream);
      return await applyCaptureResolution(rear);
    } catch (err) {
      // Rear open failed — keep the working (front) stream; the user can switch.
      if (diagEnabled()) console.warn("[camera] rear deviceId open failed", err);
    }
  }
  return await applyCaptureResolution(stream);
}

/**
 * All video-input devices. Labels are only populated once camera permission has
 * been granted, so call this AFTER `startRearCamera`. Returns [] where the API
 * is unavailable (e.g. jsdom). Used to drive the manual camera switcher.
 */
export async function listVideoInputs(): Promise<MediaDeviceInfo[]> {
  const md = navigator.mediaDevices;
  if (!md?.enumerateDevices) return [];
  const devices = await md.enumerateDevices();
  return devices.filter((d) => d.kind === "videoinput");
}

/** Open a specific camera by deviceId — backs the manual "switch camera" UI. */
export async function startCameraByDeviceId(deviceId: string): Promise<MediaStream> {
  const md = navigator.mediaDevices;
  if (!md?.getUserMedia) {
    throw new Error("This browser does not support camera capture.");
  }
  return logAcquired(
    await md.getUserMedia({ video: { deviceId: { exact: deviceId }, ...captureVideoSize() }, audio: false }),
    "device-id",
  );
}

/**
 * Open a camera by facingMode ("user"/"environment") — the switcher's fallback
 * when the device exposes fewer than two enumerable inputs (some iOS setups),
 * so front⇄back still toggles. Advisory, not `{ exact }`, so it never throws.
 */
export async function startCameraByFacing(facing: "user" | "environment"): Promise<MediaStream> {
  const md = navigator.mediaDevices;
  if (!md?.getUserMedia) {
    throw new Error("This browser does not support camera capture.");
  }
  return logAcquired(
    await md.getUserMedia({ video: { facingMode: facing, ...captureVideoSize() }, audio: false }),
    `facing-${facing}`,
  );
}

/**
 * Open the best camera for a target facing — backs the viewfinder's flip button
 * as a true front⇄back toggle. iOS/Android expose the main / ultra-wide /
 * telephoto rear lenses as separate enumerable devices, so a blind device-index
 * cycle lands on another REAR lens before it reaches the front one (the "two
 * taps to reach the selfie camera" bug). We select by localised label instead:
 * for the front target, the first front-labelled device; for the rear target,
 * the first rear lens that ISN'T an ultra-wide/telephoto variant (so switching
 * back returns to the main lens the A4-pose solve is calibrated for, not the
 * distorted ultra-wide). Falls back to "any device that isn't the current one"
 * when labels are unavailable, and finally to a plain facingMode request (some
 * iOS setups expose too few enumerable inputs to pick by id).
 */
export async function startCameraForFacing(
  target: "user" | "environment",
  currentDeviceId: string | null,
): Promise<MediaStream> {
  const cams = await listVideoInputs();
  const labelled =
    target === "environment"
      ? cams.filter((c) => REAR_LABEL_RE.test(c.label))
      : cams.filter((c) => FRONT_LABEL_RE.test(c.label));

  let pick: MediaDeviceInfo | undefined;
  if (target === "environment") {
    pick = labelled.find((c) => !REAR_AVOID_RE.test(c.label)) ?? labelled[0];
  } else {
    pick = labelled[0];
  }
  // No usable label match (unlabelled devices, or too few enumerable inputs) —
  // take any camera that isn't the current one so the toggle still moves.
  if (!pick) pick = cams.find((c) => c.deviceId && c.deviceId !== currentDeviceId);

  if (pick?.deviceId) {
    try {
      return await startCameraByDeviceId(pick.deviceId);
    } catch (err) {
      if (diagEnabled()) console.warn("[camera] switch by deviceId failed", err);
    }
  }
  // Last resort: advisory facingMode toggle (never `{ exact }`, so it can't throw).
  return await startCameraByFacing(target);
}

/** Stop every track on a stream — call before dropping the camera. */
export function stopStream(stream: MediaStream | null | undefined): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Active-lens facts for the diagnostics panel + capture-meta.json diag block. */
export interface StreamInfo {
  /** Localised lens label, e.g. "Back Camera" / "Rückkamera" — the key datum. */
  label: string;
  facingMode: string | null;
  deviceId: string | null;
  width: number | null;
  height: number | null;
  /**
   * Track zoom factor where the browser reports it (Safari 17+, Chrome). Above
   * 1 means the sensor mode is cropped, which is how the 1080p heel cut shows.
   */
  zoom?: number | null;
}

/** Read the active video track's lens facts (best-effort; empty when no track). */
export function streamInfo(stream: MediaStream | null | undefined): StreamInfo {
  const track = stream?.getVideoTracks?.()[0];
  const s = (track?.getSettings?.() ?? {}) as MediaTrackSettings & { zoom?: number };
  const info: StreamInfo = {
    label: track?.label ?? "",
    facingMode: (s.facingMode as string | undefined) ?? null,
    deviceId: (s.deviceId as string | undefined) ?? null,
    width: (s.width as number | undefined) ?? null,
    height: (s.height as number | undefined) ?? null,
  };
  if (typeof s.zoom === "number") info.zoom = s.zoom;
  return info;
}

/** All video-input labels (for the diag panel's "every lens the device exposes"). */
export async function listCameraLabels(): Promise<string[]> {
  const cams = await listVideoInputs();
  return cams.map((c) => c.label || "(unlabeled)");
}

/**
 * Freeze the current video frame into a canvas at the track's full resolution.
 * One `drawImage`, so the shutter costs no encode: the square and the full frame
 * are both cut from this snapshot afterwards and show the same instant. Release
 * it with `releaseSnapshot` once both are encoded. Throws if the frame isn't
 * ready yet.
 */
export function snapshotFrame(video: HTMLVideoElement): HTMLCanvasElement {
  const vw = video.videoWidth;
  const vh = video.videoHeight;
  if (!vw || !vh) throw new Error("Camera frame not ready yet, try again.");
  const canvas = document.createElement("canvas");
  canvas.width = vw;
  canvas.height = vh;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d canvas context");
  ctx.drawImage(video, 0, 0);
  return canvas;
}

/** Hand a snapshot's backing store back now instead of at GC (see drawJpeg). */
export function releaseSnapshot(canvas: HTMLCanvasElement): void {
  canvas.width = 0;
  canvas.height = 0;
}

/**
 * Grab the current video frame, center-crop it to a square, scale it down to
 * at most `MAX_SQUARE_DIMENSION`, and encode a JPEG blob at `quality`
 * (prototype used 0.9). Throws if the frame isn't ready yet.
 */
export async function captureSquareJpeg(
  source: FrameSource,
  quality = 0.9,
): Promise<Blob> {
  const [vw, vh] = frameSize(source);
  if (!vw || !vh) throw new Error("Camera frame not ready yet — try again.");

  const { sx, sy, size } = centerCropRect(vw, vh);
  const out = Math.min(size, MAX_SQUARE_DIMENSION);
  return await drawJpeg(source, sx, sy, size, size, quality, out, out);
}

/**
 * Grab the current video frame uncropped, at the track's full resolution, as a
 * JPEG. The IBV path's companion to `captureSquareJpeg`: IBV needs all four
 * sheet corners, which the square cuts off.
 */
export async function captureFullFrameJpeg(
  source: FrameSource,
  quality = 0.9,
): Promise<Blob> {
  const [vw, vh] = frameSize(source);
  if (!vw || !vh) throw new Error("Camera frame not ready yet — try again.");
  // A snapshot already holds the full frame: encode it as is instead of
  // copying ~12 MP into a second canvas.
  if (source instanceof HTMLCanvasElement) return await toJpeg(source, quality);
  return await drawJpeg(source, 0, 0, vw, vh, quality);
}

/** A live video frame or a `snapshotFrame` canvas. */
export type FrameSource = HTMLVideoElement | HTMLCanvasElement;

function frameSize(source: FrameSource): [number, number] {
  return source instanceof HTMLCanvasElement
    ? [source.width, source.height]
    : [source.videoWidth, source.videoHeight];
}

function toJpeg(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null"))),
      "image/jpeg",
      quality,
    );
  });
}

async function drawJpeg(
  video: FrameSource,
  sx: number,
  sy: number,
  width: number,
  height: number,
  quality: number,
  outWidth = width,
  outHeight = height,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = outWidth;
  canvas.height = outHeight;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get 2d canvas context");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(video, sx, sy, width, height, 0, 0, outWidth, outHeight);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        // Hand the backing store back now instead of at GC: at `?track=4032`
        // each shot draws two ~12 MP canvases, and iOS refuses new canvases
        // (toBlob → null) once its total canvas memory cap is reached.
        canvas.width = 0;
        canvas.height = 0;
        if (b) resolve(b);
        else reject(new Error("canvas.toBlob returned null"));
      },
      "image/jpeg",
      quality,
    );
  });
}

/**
 * Map the 6 captured frames into the upload-pipeline input shape. Filenames
 * come from each step (the backend-grouping contract); content-type is always
 * image/jpeg for captured frames.
 */
export function captureResultsToInputs(results: readonly CaptureResult[]): RunInput[] {
  const squares = results.map((r) => ({
    blob: r.blob,
    filename: r.step.filename,
    contentType: "image/jpeg",
  }));
  const fullFrames = results.flatMap((r) =>
    r.fullBlob
      ? [{ blob: r.fullBlob, filename: fullFrameFilename(r.step), contentType: "image/jpeg" }]
      : [],
  );
  return [...squares, ...fullFrames];
}

/**
 * Whether the capture shutter may fire. Blocked while a modal is open or a shot
 * is in flight (`blocked`), and — when live guidance is active — blocked on a
 * "warn": foot + paper detected, correct foot, paper framed (not too
 * large/small/edge), and, when the device reports orientation, the tilt angle
 * inside the photo's window (`ANGLE_WINDOWS`). Only "warn" blocks: "good" is positive feedback and "advisory"
 * (e.g. uneven paper lighting) is a non-blocking hint, so neither locks the
 * shutter. `guidanceSeverity` is `null` when guidance is inactive (no model,
 * `?ai=none`, still loading, or no analysed frame yet) — the shutter then stays
 * enabled so guidance can never trap the user.
 *
 * Stricter than the original prototype, which gated the shutter on the tilt
 * angle alone and kept foot/paper detection advisory.
 */
export function shutterEnabled(
  blocked: boolean,
  guidanceSeverity: GuidanceSeverity | null,
): boolean {
  return !blocked && guidanceSeverity !== "warn";
}

/**
 * Capture-side metadata uploaded alongside the photos as `capture-meta.json`,
 * consumed by footscan-server for metric scale. Carries:
 *   - `paperFormat`: the operator-picked reference sheet (A4 / Letter) → sheet
 *     dimensions for the solvePnP pose (footscan-server #46).
 *   - `sensorCropFraction`: `size / max(videoWidth, videoHeight)` of the crop →
 *     scales the focal length for the pose (footscan-server #45). Omitted when
 *     the source frame dimensions were unavailable; the server then falls back
 *     to its 16:9 assumption.
 *   - `angles` (optional): per-photo camera tilt at shot time, keyed by upload
 *     filename (see `captureAngles`). The archived prototype sent this per
 *     image; the server ignores it today but may use it as a pose prior.
 *     Omitted when no photo had an orientation reading.
 *   - `gravity` (optional): per-photo gravity vector in g, keyed like
 *     `angles` (see `captureGravity`, `gravity.ts` for axes and sign). Only
 *     footscan-server's IBV step reads it. Omitted without motion data.
 *   - `diag` (optional): on-device diagnostics (lens label, resolution,
 *     enumerated lenses, platform) — present ONLY when the tester turned diag
 *     mode on. Ignored by the server; there so a scan captured during testing
 *     carries its camera facts for later inspection.
 *   - `device` (optional): phone + rear-lens facts on EVERY scan, not just diag
 *     ones (see `device.ts`). Capture problems are device-shaped, and the phone
 *     is gone by the time a scan looks wrong. Also ignored by the server.
 */
export function paperMetaInput(
  paperFormat: string,
  sensorCropFraction?: number,
  angles?: Record<string, number>,
  diag?: Record<string, unknown>,
  device?: DeviceInfo | null,
  gravity?: Record<string, [number, number, number]>,
): RunInput {
  const meta: {
    paperFormat: string;
    sensorCropFraction?: number;
    angles?: Record<string, number>;
    gravity?: Record<string, [number, number, number]>;
    diag?: Record<string, unknown>;
    device?: DeviceInfo;
  } = { paperFormat };
  if (typeof sensorCropFraction === "number" && Number.isFinite(sensorCropFraction)) {
    meta.sensorCropFraction = sensorCropFraction;
  }
  if (angles && Object.keys(angles).length > 0) {
    meta.angles = angles;
  }
  if (gravity && Object.keys(gravity).length > 0) {
    meta.gravity = gravity;
  }
  // Diagnostics ride along ONLY when the tester enabled diag mode — normal
  // customer scans keep the clean two-field sidecar. The server reads just
  // paperFormat + sensorCropFraction and ignores this extra key.
  if (diag && Object.keys(diag).length > 0) {
    meta.diag = diag;
  }
  if (device) {
    meta.device = device;
  }
  const body = JSON.stringify(meta);
  return {
    blob: new Blob([body], { type: "application/json" }),
    filename: "capture-meta.json",
    contentType: "application/json",
  };
}
